import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';

const AmbienceSection = ({ translations = {} }) => {
  const [drumData, setDrumData] = useState(null);
  const [isInteracting, setIsInteracting] = useState(false);
  const [drumError, setDrumError] = useState(null);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const lottieRef = useRef(null);
  const audioRef = useRef(null);
  const touchStartTimeRef = useRef(null);
  const longPressTimeoutRef = useRef(null);
  const isLongPressRef = useRef(false);

  // Load drum animation
  useEffect(() => {
    fetch('/images/drum.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load drum.json');
        return res.json();
      })
      .then((data) => setDrumData(data))
      .catch((err) => {
        console.error('Error loading drum.json:', err);
        setDrumError(err.message);
      });
  }, []);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio('/sounds/drum.mp3');
    audioRef.current.loop = false;
    audioRef.current.preload = 'auto';
    audioRef.current.volume = 1;

    const handleAudioEnd = () => {
      if (isLongPressRef.current) {
        // Long press - continue playback
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(console.error);
      } else {
        // Single playback for drum
        setIsInteracting(false);
      }
    };

    audioRef.current.addEventListener('ended', handleAudioEnd);
    
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleAudioEnd);
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
  }, []);

  const initializeAudio = () => {
    if (!audioInitialized && audioRef.current) {
      audioRef.current.play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setAudioInitialized(true);
        })
        .catch(console.error);
    }
  };

  // Control animation
  useEffect(() => {
    if (lottieRef.current) {
      if (isInteracting) {
        lottieRef.current.play();
      } else {
        lottieRef.current.stop();
      }
    }
  }, [isInteracting]);

  const startAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(console.error);
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleInteractionStart = () => {
    initializeAudio();
    setIsInteracting(true);
    isLongPressRef.current = true;
    startAudio();
  };

  const handleInteractionEnd = () => {
    setIsInteracting(false);
    isLongPressRef.current = false;
    stopAudio();
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    initializeAudio();
    touchStartTimeRef.current = Date.now();
    
    longPressTimeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      handleInteractionStart();
    }, 500);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    clearTimeout(longPressTimeoutRef.current);
    
    if (isLongPressRef.current) {
      handleInteractionEnd();
    } else {
      // Short click/touch - play once
      isLongPressRef.current = false;
      setIsInteracting(true);
      startAudio();
    }
  };

  const handleClick = () => {
    initializeAudio();
    isLongPressRef.current = false;
    setIsInteracting(true);
    startAudio();
  };

  return (
    <section className="py-20 bg-[#FFF8F0] text-gray-800 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10 max-w-4xl">
        <h2 className={`text-3xl md:text-4xl font-bold text-center text-[#FF4C29] mb-12 animate-fade-in ${isInteracting ? 'animate-vibrate' : ''}`}>
          {translations.ambienceTitle || 'Our Ambience'}
        </h2>

        <div className="text-center space-y-6 animate-slide-in-left">
          <p className={`text-base md:text-lg leading-relaxed max-w-3xl mx-auto ${isInteracting ? 'animate-vibrate' : ''}`}>
            {translations.ambienceParagraph1 ||
              'Step into El Latino and immerse yourself in an atmosphere designed to delight your senses. Our decor blends rustic charm with modern elegance, featuring vibrant murals, comfortable seating, and warm lighting that invites you to relax and linger.'}
          </p>
          <p className={`text-base md:text-lg leading-relaxed max-w-3xl mx-auto ${isInteracting ? 'animate-vibrate' : ''}`}>
            {translations.ambienceParagraph2 ||
              'The air is filled with the rhythmic sounds of Latin music, creating a lively backdrop for conversations and connections. Whether you are here for a quick coffee, a leisurely meal, or a vibrant evening, our ambience ensures a memorable visit.'}
          </p>
          {drumError ? (
            <div className="mt-8 text-red-500 text-center">Error loading drum animation: {drumError}</div>
          ) : (
            drumData && (
              <div className="flex justify-center mt-8">
                <div
                  className="w-24 h-24 md:w-32 md:h-32 cursor-pointer transition-transform hover:scale-110 animate-pulse"
                  onMouseEnter={handleInteractionStart}
                  onMouseLeave={handleInteractionEnd}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onClick={handleClick}
                >
                  <Lottie 
                    lottieRef={lottieRef}
                    animationData={drumData} 
                    loop={true} 
                    autoplay={false}
                  />
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes vibrate {
            0% { transform: translate(0); }
            20% { transform: translate(-2px, 2px); }
            40% { transform: translate(-2px, -2px); }
            60% { transform: translate(2px, 2px); }
            80% { transform: translate(2px, -2px); }
            100% { transform: translate(0); }
          }

          .animate-vibrate {
            animation: vibrate 0.3s linear infinite;
          }

          .animate-fade-in {
            animation: fade-in 1s ease-out forwards;
          }

          @keyframes fade-in {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          .animate-slide-in-left {
            animation: slide-in-left 1s ease-out forwards;
          }

          @keyframes slide-in-left {
            0% { transform: translateX(-50px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }

          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }

          .animate-pulse {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
    </section>
  );
};

export default AmbienceSection;